import React, { useEffect, useRef, useState } from "react";
import "./ShopProductGallery.css";

/**
 * A-006.3 — Product detail image gallery with thumbs, prev/next,
 * swipe, and keyboard navigation (react-router 4.3.1 safe).
 */
function ShopProductGallery({ images, productName }) {
  const items = Array.isArray(images) ? images.filter((img) => img && img.path) : [];
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);
  const thumbsRef = useRef(null);

  useEffect(() => {
    setIndex(0);
  }, [items.length, productName]);

  useEffect(() => {
    if (index >= items.length && items.length > 0) {
      setIndex(0);
    }
  }, [index, items.length]);

  const safeIndex =
    items.length > 0 ? Math.max(0, Math.min(index, items.length - 1)) : 0;
  const current = items[safeIndex];
  const hasMultiple = items.length > 1;

  useEffect(() => {
    if (!hasMultiple || !thumbsRef.current) return;
    const active = thumbsRef.current.querySelector(".is-active");
    if (active && typeof active.scrollIntoView === "function") {
      try {
        active.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        });
      } catch (e) {
        // ignore
      }
    }
  }, [safeIndex, hasMultiple]);

  // Prefetch neighbors for snappier prev/next.
  useEffect(() => {
    if (!hasMultiple || typeof Image === "undefined" || !items.length) return;
    [-1, 1].forEach((delta) => {
      const neighbor =
        items[(safeIndex + delta + items.length) % items.length];
      if (neighbor && neighbor.path) {
        const img = new Image();
        img.src = neighbor.path;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeIndex, hasMultiple, items.length]);

  if (!items.length) {
    return (
      <div
        className="shop-product-gallery shop-product-gallery--empty"
        role="img"
        aria-label={productName || "Product image unavailable"}
      >
        <div className="shop-product-gallery__placeholder" />
      </div>
    );
  }

  const alt = current.alt_text || productName || "Product image";

  const goTo = (next) => {
    if (!hasMultiple) return;
    const total = items.length;
    setIndex(((next % total) + total) % total);
  };

  const onKeyDown = (e) => {
    if (!hasMultiple) return;
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      goTo(safeIndex - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      goTo(safeIndex + 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      goTo(0);
    } else if (e.key === "End") {
      e.preventDefault();
      goTo(items.length - 1);
    }
  };

  const onTouchStart = (e) => {
    if (!hasMultiple || !e.touches || !e.touches[0]) return;
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e) => {
    if (!hasMultiple || touchStartX.current == null) return;
    const endX =
      e.changedTouches && e.changedTouches[0]
        ? e.changedTouches[0].clientX
        : null;
    if (endX == null) return;
    const delta = endX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 40) return;
    if (delta > 0) goTo(safeIndex - 1);
    else goTo(safeIndex + 1);
  };

  return (
    <div
      className={`shop-product-gallery${
        hasMultiple ? " shop-product-gallery--multi" : ""
      }`}
    >
      <div
        className="shop-product-gallery__stage"
        tabIndex={0}
        role="region"
        aria-roledescription="carousel"
        aria-label={`${productName || "Product"} image gallery`}
        aria-live="polite"
        onKeyDown={onKeyDown}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="shop-product-gallery__frame"
          key={`frame-${current.path || safeIndex}`}
        >
          <img
            className="shop-product-gallery__image"
            src={current.path}
            alt={alt}
            loading={safeIndex === 0 ? "eager" : "lazy"}
            decoding="async"
            onError={(e) => {
              e.target.style.visibility = "hidden";
              if (e.target.parentElement) {
                e.target.parentElement.classList.add(
                  "shop-product-gallery__frame--missing"
                );
              }
            }}
          />
        </div>

        {hasMultiple ? (
          <>
            <button
              type="button"
              className="shop-product-gallery__nav shop-product-gallery__nav--prev"
              onClick={() => goTo(safeIndex - 1)}
              aria-label="Previous image"
            >
              <span aria-hidden="true">‹</span>
            </button>
            <button
              type="button"
              className="shop-product-gallery__nav shop-product-gallery__nav--next"
              onClick={() => goTo(safeIndex + 1)}
              aria-label="Next image"
            >
              <span aria-hidden="true">›</span>
            </button>
            <p className="shop-product-gallery__status" aria-hidden="true">
              {safeIndex + 1} / {items.length}
            </p>
            <span className="sr-only">
              Image {safeIndex + 1} of {items.length}
            </span>
          </>
        ) : null}
      </div>

      {hasMultiple ? (
        <ul
          className="shop-product-gallery__thumbs"
          role="list"
          ref={thumbsRef}
          aria-label="Product image thumbnails"
        >
          {items.map((item, i) => {
            const selected = i === safeIndex;
            return (
              <li key={`${item.id || item.path}-${i}`}>
                <button
                  type="button"
                  className={`shop-product-gallery__thumb${
                    selected ? " is-active" : ""
                  }`}
                  onClick={() => setIndex(i)}
                  aria-label={`Show image ${i + 1} of ${items.length}`}
                  aria-current={selected ? "true" : undefined}
                >
                  <img src={item.path} alt="" loading="lazy" decoding="async" />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

export default ShopProductGallery;
